import React from 'react';
import { Box, SxProps } from '@mui/material';

/* ─── Thai ประจำยาม (Pra Jum Yam) Motif Components ─── */

// Pointed star — four concave petals
export const ThaiStar: React.FC<{
  size: number;
  color?: string;
  opacity?: number;
  rotate?: number;
  sx?: SxProps;
}> = ({ size, color = '#5C3D2E', opacity = 0.06, rotate = 0, sx }) => (
  <Box
    sx={{
      position: 'absolute',
      width: size,
      height: size,
      opacity,
      pointerEvents: 'none',
      ...sx,
    }}
  >
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 100 100"
      style={{ transform: `rotate(${rotate}deg)` }}
    >
      <path
        d="M50,2 Q40,40 2,50 Q40,60 50,98 Q60,60 98,50 Q60,40 50,2Z"
        fill={color}
      />
    </svg>
  </Box>
);

// Quatrefoil — four overlapping circles (ดอกจัน)
export const ThaiQuatrefoil: React.FC<{
  size: number;
  color?: string;
  opacity?: number;
  strokeOnly?: boolean;
  strokeWidth?: number;
  sx?: SxProps;
}> = ({
  size,
  color = '#5C3D2E',
  opacity = 0.06,
  strokeOnly = false,
  strokeWidth = 2.5,
  sx,
}) => (
  <Box
    sx={{
      position: 'absolute',
      width: size,
      height: size,
      opacity,
      pointerEvents: 'none',
      ...sx,
    }}
  >
    <svg width="100%" height="100%" viewBox="0 0 100 100">
      <g
        fill={strokeOnly ? 'none' : color}
        stroke={strokeOnly ? color : 'none'}
        strokeWidth={strokeOnly ? strokeWidth : 0}
      >
        <circle cx="50" cy="28" r="22" />
        <circle cx="72" cy="50" r="22" />
        <circle cx="50" cy="72" r="22" />
        <circle cx="28" cy="50" r="22" />
      </g>
    </svg>
  </Box>
);

// Nested motif — star with inner quatrefoil cutout
export const ThaiNestedMotif: React.FC<{
  size: number;
  outerColor?: string;
  innerColor?: string;
  opacity?: number;
  sx?: SxProps;
}> = ({
  size,
  outerColor = '#5C3D2E',
  innerColor = '#FFFFFF',
  opacity = 0.08,
  sx,
}) => (
  <Box
    sx={{
      position: 'absolute',
      width: size,
      height: size,
      opacity,
      pointerEvents: 'none',
      ...sx,
    }}
  >
    <svg width="100%" height="100%" viewBox="0 0 100 100">
      {/* Outer pointed star */}
      <path
        d="M50,2 Q40,40 2,50 Q40,60 50,98 Q60,60 98,50 Q60,40 50,2Z"
        fill={outerColor}
      />
      {/* Inner quatrefoil cutout */}
      <g fill={innerColor}>
        <circle cx="50" cy="35" r="12" />
        <circle cx="65" cy="50" r="12" />
        <circle cx="50" cy="65" r="12" />
        <circle cx="35" cy="50" r="12" />
      </g>
    </svg>
  </Box>
);

// Thai ornamental divider — replaces the plain diamond CSS divider
export const ThaiDivider: React.FC<{ color?: string }> = ({
  color = '#C8A24D',
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      my: 2,
      width: 120,
      mx: 'auto',
    }}
  >
    {/* Left line */}
    <Box
      sx={{
        flex: 1,
        height: '1px',
        background: `linear-gradient(90deg, transparent, ${color})`,
      }}
    />
    {/* Center Thai mini-quatrefoil */}
    <svg width="18" height="18" viewBox="0 0 100 100">
      <g fill={color}>
        <circle cx="50" cy="28" r="20" />
        <circle cx="72" cy="50" r="20" />
        <circle cx="50" cy="72" r="20" />
        <circle cx="28" cy="50" r="20" />
      </g>
    </svg>
    {/* Right line */}
    <Box
      sx={{
        flex: 1,
        height: '1px',
        background: `linear-gradient(90deg, ${color}, transparent)`,
      }}
    />
  </Box>
);
